import { ReactNode, Suspense, lazy, useEffect, useState } from 'react';

import { RiFileLine } from '@remixicon/react';

import {
  ButtonIcon,
  Focusable,
  IconDownload,
  IconExternalLink,
  Text,
} from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';
import {
  Copy,
  DateUtils,
  DialogCompositeMui as DialogComposite,
  ErrorBoundary,
  Spinner,
  downloadTextFile,
  sanitizeFilename,
  useDialog,
  useDocumentNavigation,
} from '@spotify/confidence-core-react';
import {
  CopyAsMarkdownButton,
  useCollaborativeDocument,
} from '@spotify/confidence-editor-react';
import {
  AttachmentFragment,
  getTypeOrNull,
  useCollaborativeDocumentQuery,
} from '@spotify/confidence-graphql';
import {
  ArtifactCardBase,
  ArtifactIcon,
  ArtifactListItem,
  ArtifactMenu,
  createArtifactExtension,
  createModifierClickHandler,
  getArtifactDisplayName,
  isExternalLink,
} from '@spotify/plugin-confidence-artifacts-react';
import { MarkdownContent } from '@spotify/plugin-confidence-chat-react';
import { UNTITLED_DOCUMENT_TITLE } from '@spotify/plugin-confidence-collaboration-react';
import {
  IdentityAvatar,
  identityKind,
} from '@spotify/plugin-confidence-iam-react';
import { getResourceName } from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';

import styles from './DocumentArtifactExtension.module.css';

const DOCUMENT_UPDATING_POLL_INTERVAL_MS = 2_000;
const DOCUMENT_EDITOR_AVATAR_SIZE = '20px';

const LazyDocumentPreviewEditor = lazy(() => import('./DocumentPreviewEditor'));

const reflowSingleLineTables = (markdown: string): string =>
  markdown.replace(/\|(?:[^|\n]*\|)+/g, table => {
    if (table.includes('\n')) return table;
    const separator = table.match(/\|(?::?-+:?\|)+/);
    if (!separator) return table;
    const cols = (separator[0].match(/:?-+:?/g) ?? []).length;
    if (cols === 0) return table;

    const pipesPerRow = cols + 1;
    const rows: string[] = [];
    let rowStart = 0;
    let rowPipes = 0;
    for (let i = 0; i < table.length; i++) {
      if (table[i] !== '|') continue;
      rowPipes++;
      if (rowPipes === pipesPerRow) {
        rows.push(table.slice(rowStart, i + 1).trim());
        rowStart = i + 1;
        rowPipes = 0;
      }
    }
    if (rows.length < 2) return table;
    return `\n\n${rows.join('\n')}\n\n`;
  });

const hasDocumentContent = (content: unknown): boolean =>
  (typeof content === 'string' && content.trim().length > 0) ||
  (content !== null && content !== undefined && typeof content === 'object');

const DocumentArtifactContent = ({
  documentName,
}: {
  documentName: string;
}) => {
  const { collaborativeDocument, isLoading, startPolling, stopPolling } =
    useCollaborativeDocument({
      name: documentName,
    });

  const markdown = collaborativeDocument?.markdown;
  const content = collaborativeDocument?.content;
  const hasContent = hasDocumentContent(content);
  const hasPendingOperation = !!collaborativeDocument?.pendingOperation;
  const isUpdating = hasPendingOperation || (hasContent && !markdown);

  useEffect(() => {
    if (isUpdating) {
      startPolling(DOCUMENT_UPDATING_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isUpdating, startPolling, stopPolling]);

  if (isLoading && !collaborativeDocument) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  if (markdown) {
    return (
      <MarkdownContent>{reflowSingleLineTables(markdown)}</MarkdownContent>
    );
  }

  if (typeof content === 'string' && content.trim()) {
    return <MarkdownContent>{reflowSingleLineTables(content)}</MarkdownContent>;
  }

  if (isUpdating) {
    return (
      <div className={styles.loading}>
        <Spinner size={24} aria-label="Loading document" />
      </div>
    );
  }

  return (
    <div className={styles.emptyState}>
      <Text variant="bodySmall" semanticColor="textSubdued">
        This document is empty.
      </Text>
    </div>
  );
};

const DocumentArtifactPreview = ({
  documentName,
  renderActions,
}: {
  documentName: string;
  renderActions?: (documentName: string) => ReactNode;
}) => (
  <ErrorBoundary
    renderFallback={() => (
      <DocumentArtifactContent documentName={documentName} />
    )}
  >
    <Suspense
      fallback={
        <div className={styles.loading}>
          <Spinner size={24} />
        </div>
      }
    >
      <LazyDocumentPreviewEditor
        documentName={documentName}
        renderActions={renderActions}
      />
    </Suspense>
  </ErrorBoundary>
);

const isDocumentArtifact = (artifact: AttachmentFragment) =>
  !isExternalLink(artifact.uri) &&
  getResourceName(artifact.uri).startsWith('collaborativeDocuments/');

const useDocumentHref = (documentName: string) => {
  const { buildDocumentHref } = useDocumentNavigation();
  const [, documentId] = documentName.split('/');
  return documentId ? buildDocumentHref(documentId) : undefined;
};

const DocumentArtifactListItem = ({
  artifact,
  icon,
  onSelect,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onSelect: () => void;
  onDelete?: () => void;
  onRename?: () => void;
}) => {
  const [menuOpened, setMenuOpened] = useState(false);
  const documentName = getResourceName(artifact.uri);
  const documentHref = useDocumentHref(documentName);
  const displayName = getArtifactDisplayName(artifact, UNTITLED_DOCUMENT_TITLE);
  const fullDocumentUrl = documentHref
    ? `${window.location.origin}${documentHref}`
    : undefined;
  const { collaborativeDocument } = useCollaborativeDocument({
    name: documentName,
    skip: !menuOpened,
  });

  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const markdownContent =
    collaborativeDocument?.markdown ??
    (typeof collaborativeDocument?.content === 'string'
      ? collaborativeDocument.content
      : undefined);

  const handleDownloadMarkdown = () => {
    if (!markdownContent) return;
    const filename = `${sanitizeFilename(getArtifactDisplayName(artifact, 'document'))}.md`;
    downloadTextFile(markdownContent, filename);
  };

  return (
    <ArtifactListItem.Root
      onClick={createModifierClickHandler(documentHref, onSelect)}
    >
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={displayName}
        secondary={<>Document &middot; {dateLabel}</>}
      />
      <ArtifactListItem.Action>
        <ArtifactMenu.Root
          aria-label="Document actions"
          copyUrl={fullDocumentUrl}
          displayName={displayName}
          onOpen={() => setMenuOpened(true)}
          onRename={onRename}
          onDelete={onDelete}
        >
          <ArtifactMenu.Item
            icon={<RiFileLine size={16} />}
            onClick={() => {
              if (documentHref) {
                window.open(documentHref, '_blank');
              } else {
                onSelect();
              }
            }}
          >
            Open full document
          </ArtifactMenu.Item>
          <Copy
            value={() => markdownContent ?? ''}
            component={ArtifactMenu.Item}
            label="Copy as Markdown"
            icon="copy"
            disabled={!markdownContent}
          />
          <ArtifactMenu.Item
            icon={<IconDownload size="small" />}
            onClick={handleDownloadMarkdown}
            disabled={!markdownContent}
          >
            Download as Markdown
          </ArtifactMenu.Item>
        </ArtifactMenu.Root>
      </ArtifactListItem.Action>
    </ArtifactListItem.Root>
  );
};

const PopOutDocumentButton = ({ documentName }: { documentName: string }) => {
  const documentHref = useDocumentHref(documentName);

  if (!documentHref) {
    return null;
  }

  return (
    <TooltipRoot>
      <ButtonIcon
        variant="tertiary"
        onClick={() => window.open(documentHref, '_blank')}
        aria-label="Pop out document"
      >
        <IconExternalLink />
      </ButtonIcon>
      <Tooltip placement="bottom">Open full document</Tooltip>
    </TooltipRoot>
  );
};

const DocumentEditorAvatar = ({ documentName }: { documentName: string }) => {
  const { data } = useCollaborativeDocumentQuery({
    variables: { name: documentName },
    fetchPolicy: 'cache-first',
  });
  const collaborativeDocument = getTypeOrNull(data?.collaborativeDocument);
  const updater = getTypeOrNull(collaborativeDocument?.updater);
  const hasHumanUpdater = updater
    ? identityKind(updater.name) === 'user'
    : false;

  if (!collaborativeDocument?.updateTime || !hasHumanUpdater || !updater) {
    return null;
  }

  const tooltipLabel = `Last edited ${DateUtils.xAgo(new Date(collaborativeDocument.updateTime))} by ${updater.displayName}`;

  return (
    <TooltipRoot>
      <Focusable>
        <div
          className={styles.editorAvatar}
          role="img"
          aria-label={tooltipLabel}
        >
          <IdentityAvatar
            data-testid="document-editor-avatar"
            identityRef={updater}
            title=""
            size={DOCUMENT_EDITOR_AVATAR_SIZE}
          />
        </div>
      </Focusable>
      <Tooltip placement="bottom">{tooltipLabel}</Tooltip>
    </TooltipRoot>
  );
};

const DocumentArtifactHeaderActions = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const documentName = getResourceName(artifact.uri);

  return (
    <>
      <DocumentEditorAvatar documentName={documentName} />
      <PopOutDocumentButton documentName={documentName} />
    </>
  );
};

const DocumentDialogActions = ({ documentName }: { documentName: string }) => {
  return (
    <div className={styles.dialogActions}>
      <PopOutDocumentButton documentName={documentName} />
      <CopyAsMarkdownButton />
    </div>
  );
};

const DocumentArtifactCard = ({
  artifact,
  onDelete,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
}) => {
  const { openDialog, closeDialog } = useDialog();
  const documentName = getResourceName(artifact.uri);

  const handleNavigate = () => {
    openDialog({
      content: (
        <DialogComposite.Root static onClose={closeDialog}>
          <DialogComposite.Content>
            <DialogComposite.CloseButton />
            <DialogComposite.Header>
              <DialogComposite.Title>
                {getArtifactDisplayName(artifact, UNTITLED_DOCUMENT_TITLE)}
              </DialogComposite.Title>
            </DialogComposite.Header>
            <DialogComposite.Body className={styles.dialogBody}>
              <DocumentArtifactPreview
                documentName={documentName}
                renderActions={name => (
                  <DocumentDialogActions documentName={name} />
                )}
              />
            </DialogComposite.Body>
          </DialogComposite.Content>
        </DialogComposite.Root>
      ),
      dialogProps: { maxWidth: 'md', fullWidth: true },
    });
  };

  return (
    <ArtifactCardBase
      artifact={artifact}
      onNavigate={handleNavigate}
      onDelete={onDelete}
    />
  );
};

export const DocumentArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'document',
    predicate: isDocumentArtifact,
    icon: () => ({ Icon: RiFileLine, color: '#6366F1' }),
    label: 'Document',
    category: 'Documents',
    render: artifact => (
      <DocumentArtifactPreview documentName={getResourceName(artifact.uri)} />
    ),
    renderHeaderActions: artifact => (
      <DocumentArtifactHeaderActions artifact={artifact} />
    ),
    renderListItem: (artifact, { onSelect, onDelete, onRename, icon }) => (
      <DocumentArtifactListItem
        artifact={artifact}
        icon={icon}
        onSelect={onSelect}
        onDelete={onDelete}
        onRename={onRename}
      />
    ),
    renderCard: (artifact, { onDelete }) => (
      <DocumentArtifactCard artifact={artifact} onDelete={onDelete} />
    ),
  }),
);
